import { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box, Paper, Typography, Button, IconButton, Tooltip, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TableSortLabel, TablePagination, Tabs, Tab, Toolbar, Dialog, DialogTitle,
    DialogContent, DialogContentText, DialogActions, Alert, InputAdornment, TextField
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import api from "../../api";
import SnackBarComponent from "../../components/SnackBarComponent";
import { useDebounce } from "../../hooks/useDebounce";

// ... Comparator Functions ...
function descendingComparator(a, b, orderBy) {
    let valA = a[orderBy];
    let valB = b[orderBy];

    if (orderBy === 'role_name') {
        valA = a.role_name || a.name || "";
        valB = b.role_name || b.name || "";
    }

    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();

    if (valB < valA) return -1;
    if (valB > valA) return 1;
    return 0;
}
function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
    { id: "name", label: "Name" },
    { id: "email", label: "Contact" },
    { id: "city", label: "City & Vehicle" },
    { id: "epochId", label: "Day & Date" },
    { id: "start_time", label: "Time" },
    { id: "reservation_status", label: "Status" },
];

// Today at UTC midnight (ms)
function getTodayMidnightUTC() {
    const now = new Date();
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

export default function UpcomingBookings() {

    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    // Tab state: 0 = Current, 1 = Archive
    const [activeTab, setActiveTab] = useState(0);

    // Sorting state
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("name");

    // Search state
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Dialog state for deletion
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchBookings = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/api/htr/upcoming-bookings");
            const list = res.data.data ?? [];
            setBookings(list);
        } catch (err) {
            showSnackbar("Failed to load upcoming bookings.", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBookings();
    }, [fetchBookings]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleTabChange = (_, newValue) => {
        setActiveTab(newValue);
        setPage(0);
        setSearchQuery("");
    };

    const handleDeleteClick = (booking) => {
        setSelectedBooking(booking);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedBooking) return;

        setDeleting(true);
        try {
            await api.delete("/api/htr/upcoming-bookings", {
                data: {
                    phone: selectedBooking.phone,
                    slot_key: selectedBooking.slot,
                    datestamp: "DT#" + selectedBooking.epochId + "#" + selectedBooking.startTime + "#" + selectedBooking.endTime
                }
            });
            showSnackbar("Booking deleted successfully!", "success");
            setDeleteDialogOpen(false);
            fetchBookings();
        } catch (err) {
            console.error("Delete failed:", err);
            showSnackbar(err.response?.data?.msg || "Failed to delete booking.", "error");
        } finally {
            setDeleting(false);
        }
    };

    const formatTime12h = (timeStr) => {
        if (!timeStr) return "";
        let hours = parseInt(timeStr.substring(0, 2), 10);
        const minutes = timeStr.substring(2, 4);
        const suffix = hours >= 12 ? 'PM' : 'AM';
        const hours12 = ((hours % 12) || 12).toString().padStart(2, '0');
        return `${hours12}:${minutes} ${suffix}`;
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "";
        try {
            return new Date(Number(timestamp)).toLocaleString("en-US", {
                weekday: 'short', year: 'numeric', month: 'long', day: 'numeric'
            });
        } catch (e) {
            return timestamp;
        }
    }

    const visibleBookings = useMemo(() => {
        const todayMs = Date.now() - 86400000;
        const q = debouncedSearchQuery.toLowerCase();

        const filtered = bookings.filter((b) => {
            // Tab filter
            const epochMs = Number(b.epochId);
            const isCurrentTab = activeTab === 0 ? epochMs >= todayMs : epochMs < todayMs;
            if (!isCurrentTab) return false;

            // Search filter
            if (!q) return true;
            return (
                (b.name || "").toLowerCase().includes(q) ||
                (b.email || "").toLowerCase().includes(q) ||
                (b.phone || "").toLowerCase().includes(q) ||
                (b.city || "").toLowerCase().includes(q) ||
                (b.model || "").toLowerCase().includes(q) ||
                (b.reservation_status || "").toLowerCase().includes(q)
            );
        });
        return [...filtered].sort(getComparator(order, orderBy));
    }, [bookings, debouncedSearchQuery, order, orderBy, activeTab]);

    const paginatedBookings = useMemo(() => {
        return visibleBookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [visibleBookings, page, rowsPerPage]);

    return (
        <Box sx={{ width: "100%" }}>
            {snackbar.open && (
                <SnackBarComponent
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                />
            )}

            <Paper sx={{ width: "100%", mb: 2 }}>
                <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: "1 1 auto" }}>
                        <Typography variant="h6" component="div">
                            UPCOMING BOOKINGS
                        </Typography>
                    </Box>
                    <TextField
                        size="small"
                        placeholder="Search bookings…"
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ minWidth: 240 }}
                    />
                </Toolbar>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="booking tabs">
                        <Tab label="Current" id="tab-current" aria-controls="tabpanel-current" />
                        <Tab label="Archive" id="tab-archive" aria-controls="tabpanel-archive" />
                    </Tabs>
                </Box>

                <TableContainer>
                    <Table sx={{ minWidth: 650 }} aria-label="bookings table" size="medium">
                        <TableHead>
                            <TableRow>
                                {headCells.map((hc) => (
                                    <TableCell key={hc.id} sortDirection={orderBy === hc.id ? order : false}>
                                        <TableSortLabel
                                            active={orderBy === hc.id}
                                            direction={orderBy === hc.id ? order : "asc"}
                                            onClick={() => handleRequestSort(hc.id)}
                                        >
                                            <strong>{hc.label}</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size="1.5rem" />
                                    </TableCell>
                                </TableRow>
                            ) : visibleBookings.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                        <Alert severity="info" sx={{ justifyContent: "center" }}>
                                            {searchQuery
                                                ? "No bookings match your search."
                                                : activeTab === 0
                                                    ? "No current bookings found."
                                                    : "No archived bookings found."}
                                        </Alert>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedBookings.map((booking) => {
                                    return (
                                        <TableRow
                                            key={`${booking.phone}-${booking.slot}`}
                                            hover
                                            sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                        >
                                            <TableCell>{booking.name || "N/A"}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{booking.email || "N/A"}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {booking.phone || "N/A"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{booking.city}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {booking.model}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {formatDate(booking.epochId)}
                                            </TableCell>
                                            <TableCell>
                                                {formatTime12h(booking.start_time)} – {formatTime12h(booking.end_time)}
                                            </TableCell>
                                            <TableCell>{booking.reservation_status}</TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Delete Booking">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteClick(booking)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={visibleBookings.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            {/* ── Confirm Delete Dialog ── */}
            <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Delete Booking</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mt: 1 }}>
                        Are you sure you want to delete the booking for <strong>{selectedBooking?.name || selectedBooking?.phone}</strong>?
                        <br />
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
